import React, { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Dialog from "@mui/material/Dialog";
import { Slide } from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import PropTypes from "prop-types";
import MDSelect from "@mui/material/Select";
import InputLabel from "@mui/material/InputLabel";

// Material Dashboard components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";

// Layout components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

// Custom hooks
import useMemberData from "layouts/tables/data/memberTable";
import Swal from "sweetalert2";

// Correct Transition component implementation
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="down" ref={ref} {...props} />;
});
Transition.displayName = "Transition";

// Helper component for detail items in the show modal
const DetailItem = ({ label, value }) => (
  <MDBox mb={1}>
    <MDTypography variant="caption" fontWeight="bold">
      {label}:
    </MDTypography>
    <MDTypography variant="body2" ml={1}>
      {value || "N/A"}
    </MDTypography>
  </MDBox>
);

DetailItem.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.any,
};

function Member() {
  useEffect(() => {
    // Dynamically add the style when component mounts
    const style = document.createElement("style");
    style.innerHTML = `
        .swal2-container {
          z-index: 9999 !important;
        }
      `;
    document.head.appendChild(style);

    return () => {
      // Clean up when component unmounts
      document.head.removeChild(style);
    };
  }, []);

  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 3;
  const [modalIsClosing, setModalIsClosing] = useState(false);

  const {
    columns,
    rows,
    isLoading,
    error,
    handlePrint,
    editModalOpen,
    setEditModalOpen,
    deleteModalOpen,
    setDeleteModalOpen,
    showModalOpen,
    setShowModalOpen,
    selectedMember,
    setSelectedMember,
    handleEditSubmit,
    handleDeleteConfirm,
    handleAddMember,
    departments,
    ministries,
    MemberDetailsModal,
  } = useMemberData();

  const handleNext = () => {
    // Validate fields based on current step
    const errors = {
      1: validateStep1(),
      2: validateStep2(),
      3: validateStep3(),
    }[currentStep];

    if (errors) {
      showErrorToast(errors);
      return;
    }

    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const validateStep1 = () => {
    if (!selectedMember?.firstName) return "First name is required";
    if (!selectedMember?.surname) return "Surname is required";
    if (!selectedMember?.phoneNumber) return "Phone number is required";
    if (!selectedMember?.gender) return "Gender is required";
    if (!selectedMember?.residentialAddress) return "Residential address is required";
    if (!selectedMember?.region) return "Region is required";
    if (!selectedMember?.dateOfBirth) return "Date of Birth is required";
    if (!selectedMember?.maritalStatus) return "Marital Status is required";
    return null;
  };

  const validateStep2 = () => {
    // if (!selectedMember?.nameOfParents) return "Parent name is required";
    if (!selectedMember?.areYouBaptized) return "Are you Baptized Field is required";
    return null;
  };

  const validateStep3 = () => {
    if (!selectedMember?.nameOfPreviousChurch) return "Name of Previous Church is required";
    if (!selectedMember?.placeOfInterest) return "Place of Interest is required";
    if (!selectedMember?.departmentId) return "Department Field is required";
    if (!selectedMember?.willYouPayTithe) return "Will you Pay Tithe is required";
    if (!selectedMember?.willYouPayWelfare) return "Will you Pay Welfare is required";
    if (!selectedMember?.membershipDate) return "Membership Date is required";
    return null;
  };

  const showErrorToast = (message) => {
    Swal.fire({
      title: "Error!",
      text: message,
      icon: "error",
      position: "top",
      toast: true,
      showConfirmButton: false,
      timer: 3000,
      timerProgressBar: true,
    });
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleCloseModal = (event, reason) => {
    // Prevent closing on backdrop click or escape key
    if (reason && (reason === "backdropClick" || reason === "escapeKeyDown")) {
      return;
    }
    setModalIsClosing(true);
    setTimeout(() => {
      setEditModalOpen(false);
      setCurrentStep(1);
      setModalIsClosing(false);
    }, 300); // Match this with your transition duration
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox pt={6} pb={3}>
          <MDTypography>Loading members...</MDTypography>
        </MDBox>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox pt={6} pb={3}>
          <MDTypography color="error">Error: {error}</MDTypography>
        </MDBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <Grid container alignItems="center" justifyContent="space-between">
                  <Grid item>
                    <MDTypography variant="h6" color="white">
                      Church Members
                    </MDTypography>
                  </Grid>
                  <Grid item>
                    <MDBox display="flex" alignItems="center" gap={1}>
                      <MDButton variant="gradient" color="dark" onClick={handleAddMember}>
                        <Icon>add</Icon>&nbsp;New Member
                      </MDButton>
                      <MDButton variant="gradient" color="dark" onClick={handlePrint}>
                        <Icon>print</Icon>
                      </MDButton>
                    </MDBox>
                  </Grid>
                </Grid>
              </MDBox>
              <MDBox pt={3}>
                <DataTable
                  table={{ columns, rows }}
                  isSorted={false}
                  entriesPerPage={true}
                  showTotalEntries={true}
                  noEndBorder
                  canSearch={true}
                  searchLabel="Search members..."
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Edit Member Modal */}
      <Dialog
        open={editModalOpen && !modalIsClosing}
        onClose={handleCloseModal}
        fullWidth={true}
        maxWidth="lg"
        TransitionComponent={Transition}
        disableAutoFocus
        disableEnforceFocus
        disableRestoreFocus
        sx={{
          "& .MuiDialog-container": {
            alignItems: "flex-start",
            marginTop: "20px",
          },
          "& .MuiPaper-root": {
            margin: 0,
            width: "100%",
            maxHeight: "calc(100vh - 40px)",
            boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
          },
        }}
        disableEscapeKeyDown={true}
        hideBackdrop={false}
        BackdropProps={{
          style: {
            backgroundColor: "rgba(0,0,0,0.5)",
            pointerEvents: editModalOpen ? "auto" : "none",
          },
        }}
      >
        <MDBox p={4}>
          <MDTypography variant="h4" gutterBottom>
            {selectedMember?.id ? "Edit Member" : "Membership Form"}
          </MDTypography>

          {/* Step 1: Basic Information */}
          {currentStep === 1 && (
            <>
              <MDTypography variant="h6" gutterBottom>
                Basic Information
              </MDTypography>
              <MDBox display="flex" gap={2} mb={2}>
                <MDInput
                  label="First Name *"
                  value={selectedMember?.firstName || ""}
                  onChange={(e) =>
                    setSelectedMember({ ...selectedMember, firstName: e.target.value })
                  }
                  fullWidth
                  margin="normal"
                  required
                  error={!selectedMember?.firstName}
                  helperText={!selectedMember?.firstName ? "This field is required" : ""}
                />

                <MDInput
                  label="Middle Name"
                  value={selectedMember?.middleName || ""}
                  onChange={(e) =>
                    setSelectedMember({ ...selectedMember, middleName: e.target.value })
                  }
                  fullWidth
                  margin="normal"
                />

                <MDInput
                  label="Surname *"
                  value={selectedMember?.surname || ""}
                  onChange={(e) =>
                    setSelectedMember({ ...selectedMember, surname: e.target.value })
                  }
                  fullWidth
                  margin="normal"
                  required
                  error={!selectedMember?.surname}
                  helperText={!selectedMember?.surname ? "This field is required" : ""}
                />
              </MDBox>
              <MDBox display="flex" gap={2} mb={2}>
                <MDInput
                  label="Residential Address *"
                  value={selectedMember?.residentialAddress || ""}
                  onChange={(e) =>
                    setSelectedMember({ ...selectedMember, residentialAddress: e.target.value })
                  }
                  fullWidth
                  margin="normal"
                  required
                  error={!selectedMember?.residentialAddress}
                  helperText={!selectedMember?.residentialAddress ? "This field is required" : ""}
                />
                <MDInput
                  label="Region"
                  value={selectedMember?.region || ""}
                  onChange={(e) => setSelectedMember({ ...selectedMember, region: e.target.value })}
                  fullWidth
                  margin="normal"
                />
                <MDInput
                  label="Home Town"
                  value={selectedMember?.homeTown || ""}
                  onChange={(e) =>
                    setSelectedMember({ ...selectedMember, homeTown: e.target.value })
                  }
                  fullWidth
                  margin="normal"
                />
                <FormControl fullWidth className="form-control" style={{ marginTop: 12 }}>
                  <InputLabel id="demo-simple-select-label">Gender</InputLabel>
                  <Select
                    labelId="demo-simple-select-label"
                    id="demo-simple-select"
                    value={selectedMember?.gender || ""}
                    label="Gender"
                    fullWidth
                    margin="normal"
                    error={!selectedMember?.gender}
                    helperText={!selectedMember?.gender ? "This field is required" : ""}
                    onChange={(e) =>
                      setSelectedMember({ ...selectedMember, gender: e.target.value })
                    }
                    sx={{ minHeight: 50 }}
                  >
                    <MenuItem value="Male">Male</MenuItem>
                    <MenuItem value="Female">Female</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </Select>
                </FormControl>
              </MDBox>
              <MDBox display="flex" gap={2} mb={2}>
                <MDInput
                  label="Phone Number *"
                  value={selectedMember?.phoneNumber || ""}
                  onChange={(e) =>
                    setSelectedMember({ ...selectedMember, phoneNumber: e.target.value })
                  }
                  fullWidth
                  margin="normal"
                  required
                  error={!selectedMember?.phoneNumber}
                  helperText={!selectedMember?.phoneNumber ? "This field is required" : ""}
                />
                <MDInput
                  label="Other Number"
                  value={selectedMember?.otherNumber || ""}
                  onChange={(e) =>
                    setSelectedMember({ ...selectedMember, otherNumber: e.target.value })
                  }
                  fullWidth
                  margin="normal"
                />
                <MDInput
                  label="Whatsapp Number"
                  value={selectedMember?.whatsappNumber || ""}
                  onChange={(e) =>
                    setSelectedMember({ ...selectedMember, whatsappNumber: e.target.value })
                  }
                  fullWidth
                  margin="normal"
                />
                <MDInput
                  label="Postal Address"
                  value={selectedMember?.postalAddress || ""}
                  onChange={(e) =>
                    setSelectedMember({ ...selectedMember, postalAddress: e.target.value })
                  }
                  fullWidth
                  margin="normal"
                />
                <MDInput
                  label="Date of Birth"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={selectedMember?.dateOfBirth || ""}
                  error={!selectedMember?.dateOfBirth}
                  helperText={!selectedMember?.dateOfBirth ? "This field is required" : ""}
                  onChange={(e) =>
                    setSelectedMember({ ...selectedMember, dateOfBirth: e.target.value })
                  }
                  fullWidth
                  margin="normal"
                />
              </MDBox>
              <MDBox display="flex" gap={2} mb={2}>
                <MDInput
                  label="Profession"
                  value={selectedMember?.profession || ""}
                  onChange={(e) =>
                    setSelectedMember({ ...selectedMember, profession: e.target.value })
                  }
                  fullWidth
                  margin="normal"
                />
                <MDInput
                  label="Rank / Position"
                  value={selectedMember?.rank || ""}
                  onChange={(e) => setSelectedMember({ ...selectedMember, rank: e.target.value })}
                  fullWidth
                  margin="normal"
                />
                <FormControl fullWidth className="form-control" style={{ marginTop: 12 }}>
                  <InputLabel id="demo-simple-select-label">Employment</InputLabel>
                  <Select
                    labelId="demo-simple-select-label"
                    id="demo-simple-select"
                    value={selectedMember?.employment || ""}
                    label="Employment"
                    fullWidth
                    margin="normal"
                    onChange={(e) =>
                      setSelectedMember({ ...selectedMember, employment: e.target.value })
                    }
                    sx={{ minHeight: 50 }}
                  >
                    <MenuItem value="Civil Servant">Civil Servant</MenuItem>
                    <MenuItem value="Employed">Employed</MenuItem>
                    <MenuItem value="Self Employed">Self Employed</MenuItem>
                    <MenuItem value="Apprentice">Apprentice</MenuItem>
                    <MenuItem value="Student">Student</MenuItem>
                    <MenuItem value="Retired">Retired</MenuItem>
                  </Select>
                </FormControl>
                <MDInput
                  label="Email"
                  value={selectedMember?.email || ""}
                  onChange={(e) => setSelectedMember({ ...selectedMember, email: e.target.value })}
                  fullWidth
                  margin="normal"
                />
              </MDBox>
              <MDBox display="flex" gap={2} mb={2}>
                <MDInput
                  label="Name of Employer"
                  value={selectedMember?.nameOfEmployer || ""}
                  onChange={(e) =>
                    setSelectedMember({ ...selectedMember, nameOfEmployer: e.target.value })
                  }
                  fullWidth
                  margin="normal"
                />
                <FormControl fullWidth className="form-control" style={{ marginTop: 12 }}>
                  <InputLabel id="demo-simple-select-label">Education</InputLabel>
                  <Select
                    labelId="demo-simple-select-label"
                    id="demo-simple-select"
                    value={selectedMember?.education || ""}
                    label="Education"
                    fullWidth
                    margin="normal"
                    onChange={(e) =>
                      setSelectedMember({ ...selectedMember, education: e.target.value })
                    }
                    sx={{ minHeight: 50 }}
                  >
                    <MenuItem value="None">None</MenuItem>
                    <MenuItem value="Basic">Basic</MenuItem>
                    <MenuItem value="Secondary">Secondary</MenuItem>
                    <MenuItem value="Tertiary">Tertiary</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </Select>
                </FormControl>
                <FormControl fullWidth className="form-control" style={{ marginTop: 12 }}>
                  <InputLabel id="demo-simple-select-label">Marital Status</InputLabel>
                  <Select
                    labelId="demo-simple-select-label"
                    id="demo-simple-select"
                    value={selectedMember?.maritalStatus || ""}
                    label="Marital Status"
                    fullWidth
                    margin="normal"
                    error={!selectedMember?.maritalStatus}
                    helperText={!selectedMember?.maritalStatus ? "This field is required" : ""}
                    onChange={(e) =>
                      setSelectedMember({ ...selectedMember, maritalStatus: e.target.value })
                    }
                    sx={{ minHeight: 50 }}
                  >
                    <MenuItem value="Single">Single</MenuItem>
                    <MenuItem value="Married">Married</MenuItem>
                    <MenuItem value="Divorced">Divorced</MenuItem>
                    <MenuItem value="Widowed">Widowed</MenuItem>
                    <MenuItem value="Separated">Separated</MenuItem>
                  </Select>
                </FormControl>
              </MDBox>
            </>
          )}

          {/* Step 2: Department Information */}
          {currentStep === 2 && (
            <>
              <MDTypography variant="h6" gutterBottom>
                Detailed Information
              </MDTypography>
              <MDBox display="flex" gap={2} mb={2}>
                <MDInput
                  label="Name of Spouse"
                  value={selectedMember?.spouseName || ""}
                  onChange={(e) =>
                    setSelectedMember({ ...selectedMember, spouseName: e.target.value })
                  }
                  fullWidth
                  margin="normal"
                />
                <MDInput
                  label="Spouse's Phone Number"
                  value={selectedMember?.spousePhoneNumber || ""}
                  onChange={(e) =>
                    setSelectedMember({ ...selectedMember, spousePhoneNumber: e.target.value })
                  }
                  fullWidth
                  margin="normal"
                />
                <MDInput
                  label="Date of Marriage"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={selectedMember?.dateOfMarriage || ""}
                  onChange={(e) =>
                    setSelectedMember({ ...selectedMember, dateOfMarriage: e.target.value })
                  }
                  fullWidth
                  margin="normal"
                />
              </MDBox>
              <MDBox display="flex" gap={2} mb={2}>
                <MDInput
                  label="By Pastor / Reverend"
                  value={selectedMember?.byPastor || ""}
                  onChange={(e) =>
                    setSelectedMember({ ...selectedMember, byPastor: e.target.value })
                  }
                  fullWidth
                  margin="normal"
                />
                <MDInput
                  label="Number of Children (If Any)"
                  type="number"
                  value={selectedMember?.numberOfChildren || ""}
                  onChange={(e) =>
                    setSelectedMember({ ...selectedMember, numberOfChildren: e.target.value })
                  }
                  fullWidth
                  margin="normal"
                />
                <MDInput
                  label="Male Children"
                  type="number"
                  value={selectedMember?.numberOfMales || ""}
                  onChange={(e) =>
                    setSelectedMember({ ...selectedMember, numberOfMales: e.target.value })
                  }
                  fullWidth
                  margin="normal"
                />
                <MDInput
                  label="Female Children"
                  type="number"
                  value={selectedMember?.numberOfFemales || ""}
                  onChange={(e) =>
                    setSelectedMember({ ...selectedMember, numberOfFemales: e.target.value })
                  }
                  fullWidth
                  margin="normal"
                />
              </MDBox>
              <MDBox display="flex" gap={2} mb={2}>
                {/* <MDInput
                  label="Names of Parents (Indicate if deceased)"
                  value={selectedMember?.nameOfParents || ""}
                  onChange={(e) =>
                    setSelectedMember({ ...selectedMember, nameOfParents: e.target.value })
                  }
                  fullWidth
                  margin="normal"
                /> */}
                <MDInput
                  label="Names of Father"
                  value={selectedMember?.nameOfFather || ""}
                  onChange={(e) =>
                    setSelectedMember({ ...selectedMember, nameOfFather: e.target.value })
                  }
                  fullWidth
                  margin="normal"
                />
                <MDInput
                  label="Father Life Status"
                  value={selectedMember?.fatherLifeStatus || ""}
                  onChange={(e) =>
                    setSelectedMember({ ...selectedMember, fatherLifeStatus: e.target.value })
                  }
                  fullWidth
                  margin="normal"
                />
                <MDInput
                  label="Names of Mother"
                  value={selectedMember?.nameOfMother || ""}
                  onChange={(e) =>
                    setSelectedMember({ ...selectedMember, nameOfMother: e.target.value })
                  }
                  fullWidth
                  margin="normal"
                />
                <MDInput
                  label="Mother Life Status"
                  value={selectedMember?.motherLifeStatus || ""}
                  onChange={(e) =>
                    setSelectedMember({ ...selectedMember, motherLifeStatus: e.target.value })
                  }
                  fullWidth
                  margin="normal"
                />
              </MDBox>
              <MDBox display="flex" gap={2} mb={2}>
                <MDInput
                  label="Next of Kin "
                  value={selectedMember?.nextOfKin || ""}
                  onChange={(e) =>
                    setSelectedMember({ ...selectedMember, nextOfKin: e.target.value })
                  }
                  fullWidth
                  margin="normal"
                />
                <MDInput
                  label="Next of Kin Relationship"
                  value={selectedMember?.nextOfKinRelationship || ""}
                  onChange={(e) =>
                    setSelectedMember({ ...selectedMember, nextOfKinRelationship: e.target.value })
                  }
                  fullWidth
                  margin="normal"
                />
                <MDInput
                  label="Next of Kin Phone Number"
                  value={selectedMember?.nextOfKinPhone || ""}
                  onChange={(e) =>
                    setSelectedMember({ ...selectedMember, nextOfKinPhone: e.target.value })
                  }
                  fullWidth
                  margin="normal"
                />
                <MDInput
                  label="Year of Conversion"
                  type="number"
                  value={selectedMember?.yearOfConversion || ""}
                  onChange={(e) =>
                    setSelectedMember({ ...selectedMember, yearOfConversion: e.target.value })
                  }
                  fullWidth
                  margin="normal"
                />
              </MDBox>
              <MDBox display="flex" gap={2} mb={2}>
                <FormControl fullWidth className="form-control" style={{ marginTop: 12 }}>
                  <InputLabel id="demo-simple-select-label">Are you Baptized</InputLabel>
                  <Select
                    labelId="demo-simple-select-label"
                    id="demo-simple-select"
                    value={selectedMember?.areYouBaptized || ""}
                    label="Are you Baptized?"
                    fullWidth
                    margin="normal"
                    onChange={(e) =>
                      setSelectedMember({ ...selectedMember, areYouBaptized: e.target.value })
                    }
                    sx={{ minHeight: 50 }}
                  >
                    <MenuItem value="Yes">Yes</MenuItem>
                    <MenuItem value="No">NO</MenuItem>
                  </Select>
                </FormControl>
                <FormControl fullWidth className="form-control" style={{ marginTop: 12 }}>
                  <InputLabel id="demo-simple-select-label">Baptism Type</InputLabel>
                  <Select
                    labelId="demo-simple-select-label"
                    id="demo-simple-select"
                    value={selectedMember?.baptismType || ""}
                    label="Baptism Type"
                    fullWidth
                    margin="normal"
                    onChange={(e) =>
                      setSelectedMember({ ...selectedMember, baptismType: e.target.value })
                    }
                    sx={{ minHeight: 50 }}
                  >
                    <MenuItem value="Immersion">Immersion</MenuItem>
                    <MenuItem value="Sprinkling">Sprinkling</MenuItem>
                    <MenuItem value="Pouring">Pouring</MenuItem>
                    <MenuItem value="Not Applicable">Not Applicable</MenuItem>
                  </Select>
                </FormControl>
                <MDInput
                  label="Date of Baptism"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={selectedMember?.baptismDate || ""}
                  onChange={(e) =>
                    setSelectedMember({ ...selectedMember, baptismDate: e.target.value })
                  }
                  fullWidth
                  margin="normal"
                />
              </MDBox>
              <MDBox display="flex" gap={2} mb={2}>
                <MDInput
                  label="By Whom"
                  value={selectedMember?.baptismBy || ""}
                  onChange={(e) =>
                    setSelectedMember({ ...selectedMember, baptismBy: e.target.value })
                  }
                  fullWidth
                  margin="normal"
                />
                <MDInput
                  label="Baptism Church"
                  value={selectedMember?.baptismChurch || ""}
                  onChange={(e) =>
                    setSelectedMember({ ...selectedMember, baptismChurch: e.target.value })
                  }
                  fullWidth
                  margin="normal"
                />
              </MDBox>
            </>
          )}

          {/* Step 3: Additional Information */}
          {currentStep === 3 && (
            <>
              <MDTypography variant="h6" gutterBottom>
                Final Information
              </MDTypography>
              <MDBox display="flex" gap={2} mb={2}>
                <MDInput
                  label="Name of Previous Church"
                  value={selectedMember?.nameOfPreviousChurch || ""}
                  onChange={(e) =>
                    setSelectedMember({ ...selectedMember, nameOfPreviousChurch: e.target.value })
                  }
                  fullWidth
                  margin="normal"
                />
                <FormControl fullWidth className="form-control" style={{ marginTop: 12 }}>
                  <InputLabel id="demo-simple-select-label">Place of Interest</InputLabel>
                  <Select
                    labelId="demo-simple-select-label"
                    id="demo-simple-select"
                    value={selectedMember?.placeOfInterest || ""}
                    label="Place of Interest"
                    fullWidth
                    margin="normal"
                    onChange={(e) =>
                      setSelectedMember({ ...selectedMember, placeOfInterest: e.target.value })
                    }
                    sx={{ minHeight: 50 }}
                  >
                    <MenuItem value="Music Ministry">Music Ministry</MenuItem>
                    <MenuItem value="Ushering">Ushering</MenuItem>
                    <MenuItem value="Prayer Tower">Prayer Tower</MenuItem>
                    <MenuItem value="Evangelism">Evangelism</MenuItem>
                    <MenuItem value="Teaching">Teaching</MenuItem>
                    <MenuItem value="Protocol">Protocol</MenuItem>
                    <MenuItem value="Outside Cleaners">Outside Cleaners</MenuItem>
                    <MenuItem value="F.O.C">F.O.C</MenuItem>
                    <MenuItem value="Janitors">Janitors</MenuItem>
                    <MenuItem value="Media">Media</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </Select>
                </FormControl>
                <FormControl fullWidth className="form-control" style={{ marginTop: 12 }}>
                  <InputLabel id="demo-simple-select-label">Department</InputLabel>
                  <Select
                    labelId="demo-simple-select-label"
                    id="demo-simple-select"
                    value={selectedMember?.departmentId || ""}
                    label="Department"
                    fullWidth
                    margin="normal"
                    error={!selectedMember?.departmentId}
                    helperText={!selectedMember?.departmentId ? "This field is required" : ""}
                    onChange={(e) =>
                      setSelectedMember({ ...selectedMember, departmentId: e.target.value })
                    }
                    sx={{ minHeight: 50 }}
                  >
                    {departments.map((dept) => (
                      <MenuItem key={dept.id} value={dept.id}>
                        {dept.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </MDBox>
              <MDBox display="flex" gap={2} mb={2}>
                <FormControl fullWidth className="form-control" style={{ marginTop: 12 }}>
                  <InputLabel id="ministry-select-label">Ministry</InputLabel>
                  <Select
                    labelId="ministry-select-label"
                    id="ministry-select"
                    value={selectedMember?.ministryId || ""}
                    label="Ministry"
                    fullWidth
                    margin="normal"
                    onChange={(e) =>
                      setSelectedMember({ ...selectedMember, ministryId: e.target.value })
                    }
                    sx={{ minHeight: 50 }}
                  >
                    {ministries.map((min) => (
                      <MenuItem key={min.id} value={min.id}>
                        {min.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth className="form-control" style={{ marginTop: 12 }}>
                  <InputLabel id="demo-simple-select-label">Will you pay Tithe?</InputLabel>
                  <Select
                    labelId="demo-simple-select-label"
                    id="demo-simple-select"
                    value={selectedMember?.willYouPayTithe || ""}
                    label="Will you pay Tithe?"
                    fullWidth
                    margin="normal"
                    onChange={(e) =>
                      setSelectedMember({ ...selectedMember, willYouPayTithe: e.target.value })
                    }
                    sx={{ minHeight: 50 }}
                  >
                    <MenuItem value="Yes">Yes</MenuItem>
                    <MenuItem value="No">No</MenuItem>
                  </Select>
                </FormControl>
                <FormControl fullWidth className="form-control" style={{ marginTop: 12 }}>
                  <InputLabel id="demo-simple-select-label">Communicant</InputLabel>
                  <Select
                    labelId="demo-simple-select-label"
                    id="demo-simple-select"
                    value={selectedMember?.communicant || ""}
                    label="Communicant"
                    fullWidth
                    margin="normal"
                    onChange={(e) =>
                      setSelectedMember({ ...selectedMember, communicant: e.target.value })
                    }
                    sx={{ minHeight: 50 }}
                  >
                    <MenuItem value="Yes">Yes</MenuItem>
                    <MenuItem value="No">No</MenuItem>
                  </Select>
                </FormControl>
                <FormControl fullWidth className="form-control" style={{ marginTop: 12 }}>
                  <InputLabel id="demo-simple-select-label">Will You Pay Welfare?</InputLabel>
                  <Select
                    labelId="demo-simple-select-label"
                    id="demo-simple-select"
                    value={selectedMember?.willYouPayWelfare || ""}
                    label="Will You Pay Welfare?"
                    fullWidth
                    margin="normal"
                    onChange={(e) =>
                      setSelectedMember({ ...selectedMember, willYouPayWelfare: e.target.value })
                    }
                    sx={{ minHeight: 50 }}
                  >
                    <MenuItem value="Yes">Yes</MenuItem>
                    <MenuItem value="No">No</MenuItem>
                  </Select>
                </FormControl>
              </MDBox>
              <MDBox display="flex" gap={2} mb={2}>
                <MDInput
                  label="Membership Date"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={selectedMember?.membershipDate || ""}
                  onChange={(e) =>
                    setSelectedMember({ ...selectedMember, membershipDate: e.target.value })
                  }
                  fullWidth
                  margin="normal"
                />
                <MDInput
                  label="Role"
                  value={selectedMember?.role || ""}
                  onChange={(e) => setSelectedMember({ ...selectedMember, role: e.target.value })}
                  fullWidth
                  margin="normal"
                />
                <MDSelect
                  label="Status"
                  value={selectedMember?.status || ""}
                  onChange={(e) => setSelectedMember({ ...selectedMember, status: e.target.value })}
                  fullWidth
                  margin="normal"
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </MDSelect>
              </MDBox>
              <MDBox display="flex" gap={2} mb={2}>
                <MDInput
                  type="file"
                  accept="image/*"
                  label="Upload Photo"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      setSelectedMember({
                        ...selectedMember,
                        photoFile: file, // Store the file object
                        photo: URL.createObjectURL(file), // For preview
                        hasNewPhoto: true, // Flag to indicate new upload
                      });
                    }
                  }}
                  fullWidth
                  margin="normal"
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
                {/* Show current photo or preview */}
                {(selectedMember?.photo || selectedMember?.photoPreview) && (
                  <div style={{ marginTop: "10px" }}>
                    <img
                      src={selectedMember.photo || selectedMember.photoPreview}
                      alt="Member"
                      style={{
                        maxWidth: "200px",
                        maxHeight: "200px",
                        border: "1px solid #ddd",
                        borderRadius: "4px",
                      }}
                    />
                  </div>
                )}
                <MDBox display="flex" gap={2} mb={2}>
                  {/* NEW: Signature as photo upload */}
                  <MDInput
                    type="file"
                    accept="image/*"
                    label="Upload Signature (Image)"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        setSelectedMember({
                          ...selectedMember,
                          signatureFile: file, // Store the signature file
                          signature: URL.createObjectURL(file), // For preview
                          hasNewSignature: true,
                        });
                      }
                    }}
                    fullWidth
                    margin="normal"
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />

                  {/* Show current signature or preview */}
                  {(selectedMember?.signature || selectedMember?.signaturePreview) && (
                    <div style={{ marginTop: "10px" }}>
                      <img
                        src={selectedMember.signature || selectedMember.signaturePreview}
                        alt="Signature"
                        style={{
                          maxWidth: "200px",
                          maxHeight: "100px",
                          border: "1px solid #ddd",
                          borderRadius: "4px",
                        }}
                      />
                    </div>
                  )}
                </MDBox>
              </MDBox>
            </>
          )}

          {/* Navigation Buttons */}
          <MDBox display="flex" justifyContent="space-between" mt={4}>
            <div>
              {currentStep > 1 && (
                <MDButton variant="gradient" color="secondary" onClick={handleBack} mr={2}>
                  Back
                </MDButton>
              )}
              {currentStep < totalSteps && (
                <MDButton
                  variant="gradient"
                  color="info"
                  onClick={handleNext}
                  style={{ marginLeft: 10 }}
                >
                  Next
                </MDButton>
              )}
            </div>

            <div>
              <MDButton
                variant="gradient"
                color="secondary"
                onClick={() => handleCloseModal(null, "buttonClick")}
                mr={2}
              >
                Cancel
              </MDButton>
              {currentStep === totalSteps && (
                <MDButton
                  variant="gradient"
                  color="success"
                  onClick={handleEditSubmit}
                  style={{ marginLeft: 10 }}
                >
                  {selectedMember?.id ? "Save Changes" : "Create Member"}
                </MDButton>
              )}
            </div>
          </MDBox>

          {/* Step Indicator */}
          <MDBox mt={2} textAlign="center">
            <MDTypography variant="button" color="text">
              Step {currentStep} of {totalSteps}
            </MDTypography>
          </MDBox>
        </MDBox>
      </Dialog>

      {/* Delete Member Modal */}
      <Dialog open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)}>
        <MDBox p={3}>
          <MDTypography variant="h6" gutterBottom>
            Confirm Delete
          </MDTypography>
          <MDTypography>
            Are you sure you want to delete &quot;{selectedMember?.memberName}&quot; member?
          </MDTypography>
          <MDBox display="flex" justifyContent="flex-end" mt={3}>
            <MDButton
              variant="gradient"
              color="secondary"
              onClick={() => setDeleteModalOpen(false)}
              mr={2}
            >
              Cancel
            </MDButton>
            <MDButton
              style={{ marginLeft: 10 }}
              variant="gradient"
              color="error"
              onClick={handleDeleteConfirm}
            >
              Confirm Delete
            </MDButton>
          </MDBox>
        </MDBox>
      </Dialog>

      {/* Show Member Details Modal */}
      <MemberDetailsModal
        member={selectedMember}
        open={showModalOpen}
        onClose={() => setShowModalOpen(false)}
      />

      <Footer />
    </DashboardLayout>
  );
}

Member.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.object),
  rows: PropTypes.arrayOf(PropTypes.object),
  isLoading: PropTypes.bool,
  error: PropTypes.string,
  handlePrint: PropTypes.func,
  editModalOpen: PropTypes.bool,
  setEditModalOpen: PropTypes.func,
  deleteModalOpen: PropTypes.bool,
  setDeleteModalOpen: PropTypes.func,
  selectedMember: PropTypes.object,
  setSelectedMember: PropTypes.func,
  handleEditSubmit: PropTypes.func,
  handleDeleteConfirm: PropTypes.func,
  handleAddMember: PropTypes.func,
};

export default Member;
